import { useCallback } from 'react';

const STORAGE_KEY = 'universal-storage-preferences';

interface StoragePreferences {
  selectedContentType: string;
  selectedCategory: string | null;
}

const defaultPreferences: StoragePreferences = {
  selectedContentType: 'all',
  selectedCategory: null,
};

export const useUniversalStoragePreferences = () => {
  const getStoredPreferences = useCallback((): StoragePreferences => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Ensure all required fields exist, fallback to defaults for missing ones
        return {
          selectedContentType: parsed.selectedContentType ?? defaultPreferences.selectedContentType,
          selectedCategory: parsed.selectedCategory ?? defaultPreferences.selectedCategory,
        };
      }
    } catch (error) {
      console.warn('[UniversalStorage] Failed to parse stored preferences:', error);
    }
    return defaultPreferences;
  }, []);

  const savePreferences = useCallback((preferences: Partial<StoragePreferences>) => {
    try {
      const current = getStoredPreferences();
      const updated = { ...current, ...preferences };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.warn('[UniversalStorage] Failed to save preferences:', error);
    }
  }, [getStoredPreferences]);

  const clearPreferences = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[UniversalStorage] Failed to clear preferences:', error);
    }
  }, []);

  return {
    getStoredPreferences,
    savePreferences,
    clearPreferences,
  };
}; 